import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Feature } from "flagged";
import { STIPA_STOI } from "../../FeatureFlags";
import { useAlerts } from "../../store/alerts/hooks";
import { useAnnotations } from "../../store/annotations/hooks";
import { useClip } from "../../store/clip/hooks";
import { useModals } from "../../store/dialogs/hooks";
import { useUploadQueue } from "../../store/uploadQueue/hooks";
import { loadCommits } from "../../pages/app/utils/dataLoader";
import { reset as exportCommitReset } from "../../store/exportCommit";
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  // AlignHorizontalCenter,
  Cancel,
  CasesRounded,
  DeleteForever,
  Hearing,
  ModeEditOutline,
  MoreVert,
  QueryStatsOutlined,
  SettingsBackupRestore,
  WatchLater,
} from "@mui/icons-material";
import {
  NOISE_FILTERING,
  UPLOADING,
  WAITING_FOR_FILES,
} from "../../store/statuses";
import {
  EditClipMetaDataDialog,
  StoiResultsDialog,
  StipaDialog,
} from "../dialogs";
import { canEditClip } from "../../store/AclUtil";
import {
  CLIP_PROCESSING_STATUSES,
  // NO_FILE_SELECTED_STATUSES,
} from "../../pages/app/utils/clipStatus";
import { useDrawer } from "../../store/drawer/hooks";

export const ClipTitleBarMenu = ({
  // clearSelectedAlignedClip,
  clip,
  color,
  isClipsPage,
  navigateOnDelete,
  onChange,
  readOnly,
  // selectedAlignedClip,
  singleChannelClips,
  user,
}) => {
  const location = useLocation();
  const showUndo =
    location.pathname.includes("/editor") ||
    location.pathname.includes("filtered/output");

  const [commits, setCommits] = useState([]);
  const { annotations } = useAnnotations();

  const isNoiseFiltering = clip?.status === NOISE_FILTERING;

  const theme = useTheme();
  const navigate = useNavigate();
  const { reset: uploadQueueReset, cancelUpload: uploadQueueCancelUpload } =
    useUploadQueue();
  const { openClipDetailsDialog, openStipaDialog, openStoiResultsDialog } =
    useModals();
  const { openStipaAlert } = useAlerts();
  const { isLoading: clipLoading, revertClip, trashClip } = useClip();
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);
  const clipIsUploading = clip?.status === UPLOADING;
  const processing = CLIP_PROCESSING_STATUSES.includes(clip?.status);
  // const noFileSelected = NO_FILE_SELECTED_STATUSES.includes(clip?.status);
  const waitingForFiles = clip?.status === WAITING_FOR_FILES;
  const hasSTOIResults = clip?.hasStoiResults;

  const { openHistoryDrawer } = useDrawer();

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);

  const handlePropertiesClick = () => {
    setAnchorEl(null);
    openClipDetailsDialog(clip?.id);
  };

  const handleDeleteClipClick = () => {
    setAnchorEl(null);
    trashClip(clip);
    // clip?.id === selectedAlignedClip && clearSelectedAlignedClip();
    onChange();
    if (navigateOnDelete) navigate(`/app/cases/${clip.caseId}/clips`);
  };

  // const handleHistoryClick = () =>
  //   navigate(`/app/cases/${clip.caseId}/clips/${clip.id}/history`);

  const handleNEWHSTORYDrawerClick = () => {
    setAnchorEl(null);
    openHistoryDrawer();
  };

  const handleExportClick = () => {
    exportCommitReset();
    navigate(`/app/cases/${clip.caseId}/clips/${clip.id}/export`);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleStipaClick = () => {
    setAnchorEl(null);
    singleChannelClips?.length >= 1
      ? openStipaDialog(clip?.id)
      : openStipaAlert();
  };

  const handleStoiResultsClick = () => {
    setAnchorEl(null);
    openStoiResultsDialog(clip?.id);
  };

  // const handleAlignClick = () => {
  //   setAnchorEl(null);
  //   navigate(`/app/cases/${clip.caseId}/clips/${clip.id}/align`);
  // };

  const handleRevertClip = async (commitId) => {
    const { commitId: prevCommitId } = commits?.at(-2);
    await revertClip(clip.caseId, clip.clipId, prevCommitId);
    navigate(0);
  };

  useEffect(() => {
    if (!showUndo) return;

    const fetchCommits = async () => {
      const snapshot = await loadCommits(clip.caseId, clip.clipId);
      setCommits(snapshot.commits);
    };
    fetchCommits();
  }, [annotations, clip, showUndo]);

  return (
    <Box display="flex" alignItems="center">
      {showUndo && (
        <Tooltip title="Revert to previous commit">
          <span>
            <IconButton
              sx={{
                color: theme.palette.common.white,
                "& .MuiIconButton-loadingIndicator": {
                  color: theme.palette.common.white,
                },
              }}
              size="large"
              aria-label="revert to previous commit"
              onClick={handleRevertClip}
              disabled={readOnly || commits?.length < 2}
              loading={clipLoading}
            >
              <SettingsBackupRestore />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title="Clip Settings">
        <IconButton
          aria-label="menu"
          aria-haspopup="true"
          onClick={handleMenuOpen}
        >
          <MoreVert
            sx={{
              color:
                color === "light"
                  ? theme.palette.primary.contrastText
                  : theme.palette.primary.dark,
              height: 1,
            }}
          />
        </IconButton>
      </Tooltip>
      {clipIsUploading ? (
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          aria-hidden={!isMenuOpen}
          disableRestoreFocus
          keepMounted
          onClose={handleMenuClose}
          open={isMenuOpen}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem
            onClick={async () => {
              await uploadQueueCancelUpload(clip?.caseId, clip?.id);
              await uploadQueueReset(clip?.id);
              setAnchorEl(null);
            }}
            sx={{ gap: 1 }}
          >
            <IconButton aria-label="Cancel Upload" color="inherit">
              <Cancel />
            </IconButton>
            Cancel Import
          </MenuItem>
        </Menu>
      ) : (
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          aria-hidden={!isMenuOpen}
          disableRestoreFocus
          keepMounted
          onClose={handleMenuClose}
          open={isMenuOpen}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem
            disabled={processing || waitingForFiles || isNoiseFiltering}
            onClick={handlePropertiesClick}
            sx={{ gap: 1 }}
          >
            <IconButton
              aria-label={`${readOnly ? "View" : "Edit"} Clip Details`}
              color="inherit"
            >
              <ModeEditOutline />
            </IconButton>
            {readOnly ? "View" : "Edit"} Clip Details
          </MenuItem>
          {isClipsPage && (
            <Feature name={STIPA_STOI}>
              <MenuItem
                disabled={processing || waitingForFiles || isNoiseFiltering}
                onClick={handleStipaClick}
                sx={{ gap: 1 }}
              >
                <IconButton aria-label="STIPA/STOI" color="inherit">
                  <Hearing />
                </IconButton>
                STOI
              </MenuItem>
            </Feature>
          )}
          {isClipsPage && hasSTOIResults && (
            <Feature name={STIPA_STOI}>
              <MenuItem
                disabled={processing || waitingForFiles || isNoiseFiltering}
                onClick={handleStoiResultsClick}
                sx={{ gap: 1 }}
              >
                <IconButton aria-label="stoi results" color="inherit">
                  <QueryStatsOutlined />
                </IconButton>
                View STOI Results
              </MenuItem>
            </Feature>
          )}
          {/* <MenuItem
            disabled={processing || waitingForFiles || noFileSelected}
            onClick={handleAlignClick}
            sx={{ gap: 1 }}
          >
            <IconButton aria-label="Align Clip" color="inherit">
              <AlignHorizontalCenter />
            </IconButton>
            Align Clip
          </MenuItem> */}
          {/* <MenuItem
            disabled={processing || waitingForFiles}
            onClick={handleHistoryClick}
            sx={{ gap: 1 }}
          >
            <IconButton aria-label="View Clip History" color="inherit">
              <WatchLater />
            </IconButton>
            View Clip History
          </MenuItem> */}
          <MenuItem
            disabled={processing || waitingForFiles}
            onClick={handleNEWHSTORYDrawerClick}
            sx={{ gap: 1 }}
          >
            <IconButton aria-label="View Clip History" color="inherit">
              <WatchLater />
            </IconButton>
            View Clip History
          </MenuItem>
          <MenuItem
            disabled={processing || waitingForFiles}
            onClick={handleExportClick}
            sx={{ gap: 1 }}
          >
            <IconButton aria-label="Export Clip" color="inherit">
              <CasesRounded />
            </IconButton>
            Export Clip
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={handleDeleteClipClick}
            disabled={readOnly || !canEditClip(user, clip) || processing}
            sx={{ color: theme.palette.error.main, gap: 1 }}
          >
            <IconButton color="inherit" aria-label="Delete Clip">
              <DeleteForever />
            </IconButton>
            Delete Clip
          </MenuItem>
        </Menu>
      )}
      <EditClipMetaDataDialog
        clip={clip}
        readOnly={!canEditClip(user, clip)}
        onChange={onChange}
      />
      <StipaDialog singleChannelClips={singleChannelClips} clip={clip} />
      <StoiResultsDialog singleChannelClips={singleChannelClips} clip={clip} />
    </Box>
  );
};
